import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('ihostpms_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const resp = await authAPI.me();
      setUser(resp.data);
    } catch {
      localStorage.removeItem('ihostpms_token');
      localStorage.removeItem('ihostpms_user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const resp = await authAPI.login({ email, password });
    const { token, user: userData } = resp.data;
    localStorage.setItem('ihostpms_token', token);
    localStorage.setItem('ihostpms_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const signup = async (data) => {
    const resp = await authAPI.signup(data);
    const { token, user: userData } = resp.data;
    localStorage.setItem('ihostpms_token', token);
    localStorage.setItem('ihostpms_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = async (sessionId) => {
    const resp = await authAPI.googleCallback(sessionId);
    const { token, user: userData } = resp.data;
    localStorage.setItem('ihostpms_token', token);
    localStorage.setItem('ihostpms_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem('ihostpms_token');
    localStorage.removeItem('ihostpms_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated: !!user,
      login,
      signup,
      loginWithGoogle,
      logout,
      refreshAuth: checkAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
